import { MButton, MDialog, MIcons, MSpinner, MTextField } from "@material-dev/insights-portal-ui-components";
import { useFormik } from "formik";
import { memo, useCallback, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@store";
import { useCreateCustomDataProjectDialogStyles } from "./CreateCustomDataProjectDialogStyles";
import {
  projectSelector,
  resetProject,
  setAddProjectDialogOpenedState,
  setOpenCreateCustomDataProject,
} from "slices/project/projectSlice";
import { initialValues, validationSchema } from "./CreateCustomDataProjectSchema";
import { LoadingState } from "types";
import { useNavigate } from "react-router";
import { CreateCustomDataProjectCommand } from "app/ApiClient";
import {
  createCustomDataProject,
  resetCreateCustomDataProjectState,
} from "slices/customDataProject/customDataProjectSlice";
import { useSelector } from "react-redux";
import { DATASETS_PATH } from "const/routes";
import { resetAddWorkdayProjectDataset, setWorkdayProjectId } from "slices/workdayProjectDataset/workdayProjectDatasetSlice";
import { resetProjectEncodingVersion } from "slices/projectEncodingVersion/projectEncodingVersionSlice";

type CreateCustomDataProjectDialogProps = {
  open: boolean;
  workdayProjectId? : number;
  onClose?: () => void;
  isWorkdayDataset:boolean;
};

export const CreateCustomDataProjectDialog = memo(({ open, workdayProjectId, onClose, isWorkdayDataset }: CreateCustomDataProjectDialogProps) => {
  const { classes: styles } = useCreateCustomDataProjectDialogStyles();
  const dispatch = useAppDispatch();
  const { isAddProjectDialogOpened } = useSelector(projectSelector);
  const { createCustomDataProjectState, customDataProject } = useAppSelector((state) => state.customDataProject);

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    (values: CreateCustomDataProjectCommand) => {
      dispatch(
        createCustomDataProject({
          ...values, workdayProjectId: workdayProjectId
        }),
      );
      dispatch(resetProjectEncodingVersion());
      dispatch(resetProject());
    },
    [onsubmit, workdayProjectId],
  );

  const formik = useFormik<CreateCustomDataProjectCommand>({
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validationSchema: validationSchema,
  });

  const closeDialog = useCallback(() => {
    if (isWorkdayDataset && onClose) {
      onClose();
    } else {
    dispatch(setOpenCreateCustomDataProject(false));
    dispatch(setAddProjectDialogOpenedState(true));
    }
    dispatch(resetAddWorkdayProjectDataset);
  }, [dispatch]);

  const getTitle = useCallback(
    () => (
      <div>
        <MButton className={styles.btnBack} onClick={closeDialog}>
          <MIcons.ArrowBack className={styles.backIcon} />
          Go back
        </MButton>
        <div className={styles.title}>New dataset</div>
      </div>
    ),
    [],
  );

  useEffect(() => {
    formik.resetForm();
  }, [isAddProjectDialogOpened]);

  useEffect(() => {
    if (createCustomDataProjectState === LoadingState.succeed) {
      dispatch(resetCreateCustomDataProjectState());
      dispatch(setOpenCreateCustomDataProject(false));
      dispatch(setAddProjectDialogOpenedState(false));
      dispatch(setWorkdayProjectId());
      if (customDataProject?.id) {
        const url = `/customProjects/${customDataProject.id}`;
        navigate(url, { replace: true });
      }
    }
  }, [createCustomDataProjectState, dispatch, customDataProject]);

  const handleCloseClick = useCallback(() => {
    if (isWorkdayDataset && onClose) {
      onClose();
    } else {
    dispatch(setWorkdayProjectId());
    dispatch(setOpenCreateCustomDataProject(false));
    const projectsPageUrl = DATASETS_PATH;
    navigate(projectsPageUrl);
    }
  }, [dispatch]);

  const getActions = useMemo(
    () => (
      <div className={styles.actions}>
        <MButton
          color="primary"
          variant="outlined"
          onClick={closeDialog}
          disabled={createCustomDataProjectState === LoadingState.pending}>
          Cancel
        </MButton>
        <MButton
          type="submit"
          color="primary"
          variant="contained"
          form="create-custom-data-project-form"
          disabled={
            createCustomDataProjectState === LoadingState.pending || Boolean(!(formik.isValid && formik.dirty))
          }>
          Create
          {createCustomDataProjectState === LoadingState.pending && (
            <MSpinner isLoading={createCustomDataProjectState === LoadingState.pending} className={styles.spinner} />
          )}
        </MButton>
      </div>
    ),
    [formik, createCustomDataProjectState],
  );

  return (
    <MDialog titleElement={getTitle()} actionsElement={getActions} open={open} onClose={handleCloseClick} fullWidth>
      <form id="create-custom-data-project-form" onSubmit={formik.handleSubmit} autoComplete="off">
        <div className={styles.root}>
          <div className={styles.formItem}>
            <div className={styles.label}>Dataset name</div>
            <MTextField
              placeholder="Enter a dataset name"
              name="projectName"
              className={styles.input}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.projectName && formik.touched.projectName)}
              value={formik.values.projectName}
              helperText={formik.touched.projectName && formik.errors.projectName ? formik.errors.projectName : ""}
              size="small"
              variant="filled"
              fullWidth
            />
          </div>
          <div className={styles.formItem}>
            <div className={styles.label}>Workday ID (optional)</div>
            <MTextField
              name="workdayId"
              className={styles.input}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.workdayId && formik.touched.workdayId)}
              value={formik.values.workdayId}
              size="small"
              variant="filled"
              fullWidth
            />
          </div>
        </div>
      </form>
    </MDialog>
  );
});
